import React, { useCallback } from "react";
import { connect } from "react-redux";

import { Col, Row } from "antd/es/grid";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import FrequencyStatus from "@mapmycustomers/shared/enum/frequency/FrequencyStatus";
import { Group } from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import Chip from "@app/component/Chip";
import DealLockNote from "@app/component/deal/DealLockNote";
import EntityBadge from "@app/component/EntityBadge";
import FrequencyIcon from "@app/component/frequency/FrequencyIcon";
import FrequencyPreviewConfig from "@app/component/frequency/type/FrequencyPreviewConfig";
import hasFrequency from "@app/component/frequency/util/hasFrequency";
import DealLockReason from "@app/enum/DealLockReason";
import Path from "@app/enum/Path";
import { getReadOnlyDealChecker } from "@app/store/deal";
import { getFrequencyModalConfig } from "@app/store/frequency";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";

import styles from "./Frequencies.module.scss";
import FrequencyBlock from "./FrequencyBlock";
import NewFrequency from "./NewFrequency";
import ProTip from "./ProTip";

interface Props {
  config: FrequencyPreviewConfig;
  isReadOnlyDeal: ReturnType<typeof getReadOnlyDealChecker>;
}

const Frequencies = ({ config, isReadOnlyDeal }: Props) => {
  const intl = useIntl();
  const {
    cadenceFromInaccessibleGroup,
    entity,
    groupsWithFrequencyConfig,
    isEntityFrequencyConfigured,
    isGroupFrequencyConfigured,
  } = config;
  const [newFrequencyVisible, showNewFrequency, hideNewFrequency] = useBoolean();
  const readOnly = isReadOnlyDeal(entity);

  const handleGroupClick = useCallback(
    (id: Group["id"]) => {
      const url = `${Path.MAP}/groups/${entity.entity}/${id}`;
      window.open(`${window.location.origin}${url}`, "_blank");
    },
    [entity]
  );

  return (
    <div className={styles.container}>
      {!isGroupFrequencyConfigured && !isEntityFrequencyConfigured ? (
        <div className={styles.noFrequency}>
          <FrequencyIcon size="xl" status={FrequencyStatus.UNKNOWN} />
          <span className={styles.noFrequencyText}>
            {intl.formatMessage({
              id: "frequency.frequencyModal.frequencies.noFrequency",
              defaultMessage: "Create a frequency to see them listed here",
              description: "frequency component - frequency modal - frequencies - no frequency",
            })}
          </span>
        </div>
      ) : (
        <div className={styles.list}>
          <div className={styles.title}>
            {intl.formatMessage({
              id: "frequency.frequencyModal.frequencies.title",
              defaultMessage: "active frequencies",
              description: "frequency component - frequency modal - frequencies - title",
            })}
          </div>
          {cadenceFromInaccessibleGroup && (
            <FrequencyBlock
              cadenceInterval={cadenceFromInaccessibleGroup.cadenceInterval}
              cadenceStatus={
                cadenceFromInaccessibleGroup.outOfCadence
                  ? FrequencyStatus.PAST_DUE
                  : FrequencyStatus.UNKNOWN
              }
              crmActivityTypeIds={[cadenceFromInaccessibleGroup.crmActivityTypeId]}
              header={
                <div className={styles.groupHeader}>
                  {intl.formatMessage({
                    id: "frequency.frequencyModal.frequencies.groups.setViaGroup.unknown",
                    defaultMessage: "Set via Group you don’t have access to view",
                    description:
                      "frequency component - frequency modal - frequencies - Set via Group - Unknown",
                  })}
                </div>
              }
              isGroup
              key="unknown-group"
              lastCompletedActivity={cadenceFromInaccessibleGroup.lastCompletedActivity}
              readonly
            />
          )}
          {groupsWithFrequencyConfig?.map((group) => (
            <FrequencyBlock
              entity={group}
              header={
                <div className={styles.groupHeader}>
                  <Row align="middle" gutter={layout.spacerM}>
                    <Col>
                      {intl.formatMessage(
                        {
                          id: "frequency.frequencyModal.frequencies.groups.setViaGroup",
                          defaultMessage: "Set via Group",
                          description:
                            "frequency component - frequency modal - frequencies - Set via Group",
                        },
                        { count: group.cadenceInterval ?? 0 }
                      )}
                    </Col>
                    <Col>
                      <Tooltip
                        title={intl.formatMessage({
                          id: "frequency.frequencyModal.frequencies.groups.tooltip",
                          defaultMessage: "Open Group in New Tab",
                          description:
                            "frequency component - frequency modal - frequencies - Group tooltip",
                        })}
                      >
                        <Chip
                          icon={<EntityBadge entity={group} hideName />}
                          onClick={() => handleGroupClick(group.id)}
                          type="light"
                        >
                          {group.name}
                        </Chip>
                      </Tooltip>
                    </Col>
                  </Row>
                </div>
              }
              isGroup
              key={group.id}
              readonly
            />
          ))}
          {hasFrequency(entity) ? (
            <FrequencyBlock
              entity={entity}
              isOpenByDefault
              lastCompletedActivity={entity.lastCompletedActivity}
              readonly={readOnly}
            />
          ) : readOnly ? (
            <DealLockNote reason={DealLockReason.STAGE_READ_ONLY} />
          ) : newFrequencyVisible ? (
            <NewFrequency onCancel={hideNewFrequency} />
          ) : (
            <Button className={styles.addFrequency} onClick={showNewFrequency}>
              {intl.formatMessage({
                id: "frequency.frequencyModal.frequencies.groups.addFrequency",
                defaultMessage: "+ Add Frequency",
                description: "frequency component - frequency modal - frequencies - Add Frequency",
              })}
            </Button>
          )}
        </div>
      )}
      <ProTip />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  config: getFrequencyModalConfig(state),
  isReadOnlyDeal: getReadOnlyDealChecker(state),
});

export default connect(mapStateToProps)(Frequencies);
